import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { notify } from '@/utils/notification';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface ClientRefundApprovalActionsProps {
  bookingId: string;
  refundReason: string;
  onStatusUpdate?: () => void;
}

export function ClientRefundApprovalActions({
  bookingId,
  refundReason,
  onStatusUpdate
}: ClientRefundApprovalActionsProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  

  const handleApprove = async () => {
    try {
      setIsSubmitting(true);

      // Call backend to process refund; backend will decide if refundable
      const { data: refundData, error: refundError } = await supabase.functions.invoke('initiate-stripe-refund', {
        body: { bookingId }
      });

      if (refundError || (refundData && refundData.error)) {
        const msg = (refundError?.message || refundData?.error || '').toString();
        console.error('Error initiating refund:', refundError || refundData);
        if (/free/i.test(msg) && /cannot be refunded/i.test(msg)) {
          notify.error('Refund not allowed', 'This booking is free and cannot be refunded.');
        } else {
          notify.error('Refund processing error', 'There was an error processing the refund with Stripe');
        }
      } else {
        console.log('Refund initiated successfully:', refundData);
        notify.success('Refund approved', 'You have approved the practitioner\'s refund request');
        if (onStatusUpdate) onStatusUpdate();
      }
    } catch (error) {
      console.error('Error approving refund:', error);
      notify.error('Error', 'Failed to approve refund. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      notify.error("Error", "Please provide a reason for rejecting the refund");
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      const { error } = await supabase
        .from('bookings')
        .update({
          refund_status: 'rejected',
          refund_rejection_reason: rejectionReason
        })
        .eq('id', bookingId);
      
      if (error) throw error;
      
      notify.success("Refund rejected", "You have rejected the practitioner's refund request");
      
      setIsRejectDialogOpen(false);
      if (onStatusUpdate) onStatusUpdate();
    } catch (error) {
      console.error('Error rejecting refund:', error);
      notify.error("Error", "Failed to reject refund. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="p-3 bg-amber-50 rounded-md mt-1">
        <p className="text-sm text-gray-700">
          <span className="font-medium">Reason: </span>
          {refundReason}
        </p>
      </div>
      
      <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2">
        <Button
          className="flex-1 bg-green-600 hover:bg-green-700"
          onClick={handleApprove}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <CheckCircle className="mr-1 h-4 w-4" />
              Approve Refund
            </>
          )}
        </Button>
        
        <AlertDialog open={isRejectDialogOpen} onOpenChange={setIsRejectDialogOpen}>
          <AlertDialogTrigger asChild>
            <Button
              variant="outline"
              className="flex-1 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
              disabled={isSubmitting}
            >
              <XCircle className="mr-1 h-4 w-4" />
              Reject Refund
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reject Refund Request</AlertDialogTitle>
              <AlertDialogDescription>
                Please provide a reason for rejecting this refund request.
                This will be shared with the practitioner.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-3">
              <Textarea
                placeholder="Reason for rejection..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="min-h-[100px]"
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isSubmitting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-600 hover:bg-red-700"
                onClick={handleReject}
                disabled={isSubmitting || !rejectionReason.trim()}
              >
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Confirm Rejection'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
